import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, Link, useOutletContext } from "react-router-dom";
import { api } from "@/lib/auth";
import { CheckCircle2, XCircle, Loader2, Copy, Gift } from "lucide-react";
import { toast } from "sonner";

function ReferralShare({ code }) {
  const link = `${window.location.origin}/refer/${code}`;
  return (
    <div className="mt-8 border-t border-white/10 pt-6 text-left" data-testid="success-referral-share">
      <div className="flex items-center gap-2 eyebrow text-emerald-400 mb-2"><Gift size={14}/> Bring a friend, both get a free class</div>
      <p className="text-zinc-400 text-sm mb-3">Share your link. When a friend books their first term, we credit both of you one free class.</p>
      <div className="flex gap-2">
        <input readOnly value={link} className="flex-1 bg-black border border-white/10 px-3 py-2 text-xs font-mono focus:border-red-600 outline-none" data-testid="success-referral-link"/>
        <button onClick={() => { navigator.clipboard.writeText(link); toast.success("Link copied"); }} className="btn-ghost !py-2 !px-3 !text-xs" data-testid="copy-referral-link"><Copy size={14}/></button>
      </div>
      <div className="text-zinc-500 text-xs mt-2">Your code: <span className="font-mono text-white">{code}</span></div>
    </div>
  );
}

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const { settings } = useOutletContext() || {};
  const sid = params.get("session_id");
  const [status, setStatus] = useState("polling");
  const [txn, setTxn] = useState(null);
  const attempts = useRef(0);

  // Runs once per session id: `attempts` is a ref (mutations must not re-trigger), `cancelled` is a local guard.
  useEffect(() => {
    if (!sid) { setStatus("error"); return; }
    let cancelled = false;
    const poll = async () => {
      if (cancelled || attempts.current > 12) { if (attempts.current > 12) setStatus("timeout"); return; }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        setTxn(data);
        if (data.payment_status === "paid") { setStatus("paid"); return; }
        if (data.payment_status === "failed" || data.payment_status === "expired") { setStatus("failed"); return; }
      } catch (e) { console.error("Payment status poll failed:", e); }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [sid]);

  return (
    <div className="min-h-[70vh] grid place-items-center p-6">
      <div className="card-surface p-12 max-w-lg w-full text-center">
        {status === "polling" && <><Loader2 size={48} className="mx-auto text-red-500 animate-spin mb-4"/><h1 className="font-display text-3xl uppercase">Confirming payment…</h1><p className="text-zinc-400 mt-2">Just a moment.</p></>}
        {status === "paid" && <><CheckCircle2 size={64} className="mx-auto text-emerald-500 mb-4"/><h1 className="font-display text-4xl uppercase">You're in!</h1><p className="text-zinc-400 mt-4">Payment received. We've emailed your confirmation and details. See you on the dance floor.</p>{txn && <div className="mt-6 text-sm text-zinc-500">Paid: ${txn.amount} {(txn.currency||"aud").toUpperCase()}</div>}{txn?.referral_code && settings?.referral_program_enabled && <ReferralShare code={txn.referral_code}/>}<Link to="/" className="btn-red mt-8 inline-flex" data-testid="back-home">Back to home</Link></>}
        {(status === "failed" || status === "error") && <><XCircle size={64} className="mx-auto text-red-500 mb-4"/><h1 className="font-display text-3xl uppercase">Payment failed</h1><p className="text-zinc-400 mt-4">Your booking is not confirmed. Please try again.</p><Link to="/classes" className="btn-red mt-8 inline-flex">Try again</Link></>}
        {status === "timeout" && <><Loader2 size={48} className="mx-auto text-amber-400 mb-4"/><h1 className="font-display text-3xl uppercase">Still processing…</h1><p className="text-zinc-400 mt-4">Payment is taking longer than usual. Check your email or contact us.</p><Link to="/" className="btn-ghost mt-6 inline-flex">Back to home</Link></>}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="min-h-[70vh] grid place-items-center p-6">
      <div className="card-surface p-12 max-w-lg w-full text-center">
        <XCircle size={64} className="mx-auto text-amber-400 mb-4"/>
        <h1 className="font-display text-3xl uppercase">Checkout cancelled</h1>
        <p className="text-zinc-400 mt-4">Your booking wasn't confirmed. You can try again — the spot is briefly reserved.</p>
        <Link to="/classes" className="btn-red mt-8 inline-flex">Back to classes</Link>
      </div>
    </div>
  );
}
